import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CorrectedValue } from "@/components/agents/configuration-server/CorrectedValue";
import type { RowActionHandlers } from "@/components/agents/configuration-server/OrgStructureListView";
import { Checkbox } from "@/components/ui/checkbox";
import { isDispatching } from "@/components/agents/configuration-server/orgUnitState";
import { OrgUnitRowActions } from "@/components/agents/configuration-server/OrgUnitRowActions";
import {
  ORG_UNIT_ACCENT,
  originAccent,
} from "@/components/agents/configuration-server/orgUnitAccent";
import {
  effectiveOrgId,
  extractedValueOf,
  orgUnitTypeLabelKey,
  type OrgTreeNode,
} from "@/lib/org-structure-tree";
import { cn } from "@/lib/utils";

function NodeName({ node }: { node: OrgTreeNode }) {
  const { t } = useTranslation();
  const name = node.name ? (
    <span>{node.name}</span>
  ) : (
    <span className="italic text-[#7b8799]">{t("name not extracted")}</span>
  );
  const extracted = extractedValueOf(node, "name");

  return extracted ? (
    <CorrectedValue extracted={extracted.value} current={name} emptyLabel={t("name not extracted")} />
  ) : (
    name
  );
}

function NodeId({ node }: { node: OrgTreeNode }) {
  const extracted = extractedValueOf(node, "id");

  return extracted ? (
    <CorrectedValue extracted={extracted.value} current={effectiveOrgId(node)} emptyLabel="—" />
  ) : (
    <span>{node.id}</span>
  );
}

function NodeCard({
  node,
  onSelect,
  actions,
  expanded,
  onToggleChildren,
}: {
  node: OrgTreeNode;
  onSelect: (node: OrgTreeNode) => void;
  actions: RowActionHandlers;
  expanded: boolean;
  onToggleChildren: () => void;
}) {
  const { t } = useTranslation();
  const unitTypeLabel = t(
    orgUnitTypeLabelKey(node.unitTypeLabel, node.levelName),
    {
      defaultValue: node.levelName,
    },
  );
  const hasChildren = node.children.length > 0;

  return (
    // Largura, padding (14px) e gap entre linhas (10px) vêm do import do MCP
    // (Organizational Structure.dc.html): 240px, p-3.5, gap-2.5. O min-height
    // é nosso — o mock nunca varia o nº de linhas (sempre label/nome/local/
    // badges), então alinha sem precisar disso; nosso "local" não existe e o
    // nome pode faltar ou ser longo, então sem um piso os cards de uma mesma
    // fileira ficam com alturas diferentes. line-clamp-2 cobre o outro lado
    // (nome grande não estica o card indefinidamente).
    // Sombra própria (a área da árvore já tem fundo cinza-claro pra dar
    // contraste, mas cartão branco sobre branco continuava plano sem isso).
    <div
      data-origin={node.origin}
      className={cn(
        // Sombra = procedência (a borda fica no cinza neutro); fundo verde =
        // já cadastrada no CBC (legenda em `OrgOriginLegend`). O botão de
        // seleção interno é transparente para o fundo do cartão aparecer.
        "relative w-[240px] overflow-hidden rounded-lg border border-[#e8ebf0] bg-white transition-shadow",
        originAccent(node.origin).glow,
        node.migrationStatus === "completed" && ORG_UNIT_ACCENT.registered,
        node.scopeVerdict === "out_of_scope" && "opacity-50",
      )}
    >
      {actions.selection && (
        <div className="px-3 pt-3">
          <Checkbox
            aria-label={t("Select {{name}}", { name: effectiveOrgId(node) })}
            checked={actions.selection.selectedKeys.has(node.key)}
            onCheckedChange={(checked) => actions.selection?.onToggle([node], checked === true)}
          />
        </div>
      )}
      <button
        type="button"
        data-testid={`org-card-${node.key}`}
        onClick={() => onSelect(node)}
        className="flex min-h-[124px] w-full flex-col gap-2.5 p-3.5 text-left transition-colors hover:bg-black/[0.02]"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#596b85]">
          {unitTypeLabel}
        </span>
        <div className="line-clamp-2 flex flex-wrap items-baseline gap-1 text-[14px] font-semibold text-[#08254c]">
          <NodeId node={node} />
          <span>·</span>
          <NodeName node={node} />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {node.country && (
            <span className="rounded-full bg-[#eef1f6] px-2 py-0.5 font-mono text-[10px] tracking-[0.08em] text-[#2c3e5b]">
              {node.country}
            </span>
          )}
        </div>
      </button>
      {/* Fora do <button> de seleção (que cobre o corpo do cartão) para não
          aninhar <button> em <button>. Os mesmos botões da linha da lista. */}
      <div className="border-t border-[#e8ebf0] px-3 py-2">
        <OrgUnitRowActions
          node={node}
          cbcConnected={actions.cbcConnected}
          runInProgressLabel={actions.runInProgressLabel}
          runBlockedReason={actions.runBlockedReason}
          dispatching={isDispatching(actions, node)}
          hideExecution={actions.hideRun}
          onRun={actions.hideRun ? undefined : actions.onRunNode}
          onRequestVerdict={actions.hideVerdict ? undefined : actions.onRequestVerdict}
          hideCrud={actions.hideCrud}
          onEdit={actions.onEditUnit}
          onDelete={actions.onDeleteUnit}
        />
      </div>
      {hasChildren && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={(event) => {
            event.stopPropagation();
            onToggleChildren();
          }}
          className="w-full border-t border-[#e8ebf0] bg-[#f8fafc] px-3 py-2 text-center text-xs font-semibold text-[#08254c] transition-colors hover:bg-[#eef4fb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#1769e0]"
        >
          {expanded
            ? t("Hide")
            : t("Expand ({{count}})", { count: node.children.length })}
        </button>
      )}
    </div>
  );
}

function CardsBranch({
  node,
  onSelect,
  actions,
}: {
  node: OrgTreeNode;
  onSelect: (node: OrgTreeNode) => void;
  actions: RowActionHandlers;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = node.children.length > 0;

  return (
    <div className="flex flex-col items-center">
      <NodeCard
        node={node}
        onSelect={onSelect}
        actions={actions}
        expanded={expanded}
        onToggleChildren={() => setExpanded((isExpanded) => !isExpanded)}
      />
      {hasChildren && expanded && (
        <div className="flex w-full flex-col items-center">
          <div
            aria-hidden="true"
            data-testid={`org-branch-connector-${node.key}`}
            className="h-6 w-px bg-[#b6c2d2]"
          />
          <div
            data-testid={`org-children-branch-${node.key}`}
            className="relative flex w-max min-w-full items-start justify-center gap-8 pt-6"
          >
            {node.children.length > 1 && (
              <span
                aria-hidden="true"
                className="absolute top-0 h-px bg-[#b6c2d2]"
                style={{ left: "120px", right: "120px" }}
              />
            )}
            {node.children.map((child) => (
              <div
                key={child.key}
                className="relative flex flex-col items-center"
              >
                <span
                  aria-hidden="true"
                  data-testid={`org-child-connector-${node.key}-${child.key}`}
                  className="absolute -top-6 h-6 w-px bg-[#b6c2d2]"
                >
                  <span className="absolute -bottom-0.5 -left-[3px] h-1.5 w-1.5 rotate-45 border-b border-r border-[#b6c2d2] bg-white" />
                </span>
                <CardsBranch
                  node={child}
                  onSelect={onSelect}
                  actions={actions}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const noop = () => {};

export function OrgStructureCardsView({
  roots,
  onSelectNode,
  // Opcionais com defaults inertes: os testes de layout deste componente não
  // montam o wiring de execução/escopo. Todo chamador real
  // (`OrgStructureTree.tsx`) passa tudo.
  onRunNode = noop,
  onRequestVerdict = noop,
  onEditUnit = noop,
  onDeleteUnit = noop,
  cbcConnected = true,
  runInProgressLabel,
  runBlockedReason,
  dispatchingUnitKeys,
  selection,
  hideRun,
  hideVerdict,
  hideCrud,
}: {
  roots: OrgTreeNode[];
  onSelectNode: (node: OrgTreeNode) => void;
  onRunNode?: (node: OrgTreeNode) => void;
  onRequestVerdict?: RowActionHandlers["onRequestVerdict"];
  onEditUnit?: (node: OrgTreeNode) => void;
  onDeleteUnit?: (node: OrgTreeNode) => void;
  cbcConnected?: boolean;
  runInProgressLabel?: string;
  runBlockedReason?: string | null;
  dispatchingUnitKeys?: ReadonlySet<string>;
  selection?: RowActionHandlers["selection"];
  hideRun?: boolean;
  /** Esconde ✓ e ⊘ de cada linha/cartão (modo execução). */
  hideVerdict?: boolean;
  /** Esconde editar e excluir de cada linha/cartão (modo execução). */
  hideCrud?: boolean;
}) {
  const actions: RowActionHandlers = {
    cbcConnected,
    runInProgressLabel,
    runBlockedReason,
    dispatchingUnitKeys,
    onRunNode,
    onRequestVerdict,
    onEditUnit,
    onDeleteUnit,
    selection,
    hideRun,
    hideVerdict,
    hideCrud,
  };

  return (
    // padding/gap do import do MCP: 24px 20px, gap 40px entre raízes. O
    // fundo cinza-claro (mesmo tom do cabeçalho da grade DE→PARA) é nosso:
    // sem ele os cartões brancos se perdiam num fundo branco atrás.
    <div className="flex gap-10 overflow-x-auto bg-[#fbfcfd] px-5 py-6">
      {roots.map((root) => (
        <CardsBranch
          key={root.key}
          node={root}
          onSelect={onSelectNode}
          actions={actions}
        />
      ))}
    </div>
  );
}
